import { Fragment, useEffect, useRef, type Ref } from "react";
import {
  ScrollView,
  StyleSheet,
  Text,
  View,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from "react-native";

import type { UiTheme } from "../theme";
import { hiddenFromScreenReader } from "./hiddenFromScreenReader";
import { useReducedMotion } from "../useReducedMotion";
import { Chip } from "./Chip";

// The frame both chip rows stand in (#336): ChipChoiceRow (one of them,
// a radiogroup) and ChipMultiRow (any of them, a group of checkboxes). The
// title above, the chips wrapped or on one line that scrolls sideways, and in
// the scrolling line the chip to follow kept in view. Split out of
// ChipChoiceRow when the second row arrived, so the scroll and its follow
// logic stay one.

export type ChipRowFrameProps<T extends string | number> = {
  theme: UiTheme;
  choices: readonly T[];
  /** The chip's a11y role: "radio" for one of them, "checkbox" for any. */
  chipRole: "radio" | "checkbox";
  isSelected: (value: T) => boolean;
  /** The tapped choice. */
  onPressChip: (value: T) => void;
  label: (value: T) => string;
  chipA11yLabel: (value: T) => string;
  /** What a chip means, read after its name (HI-8). Absent: no hint. */
  chipA11yHint?: (value: T) => string | undefined;
  /** A chip that cannot be chosen now: announced as disabled. Not dimmed, on purpose, to keep its contrast. */
  isDisabled?: (value: T) => boolean;
  /** False: no ✓ on the selected chip (Chip's `check`). */
  check?: boolean;
  /** "radiogroup" for one of them, "group" for any of them. */
  groupRole: "radiogroup" | "group";
  groupA11yLabel: string;
  title?: string;
  scroll?: boolean;
  edgeInset?: number;
  /** Scrolling line only: the chip kept on screen when it changes. */
  follow: T | null;
  /** Wrapped row only: a long label wraps on two lines inside the row's
   *  width instead of running past it (#510, large text). Default: one line. */
  wrapLabels?: boolean;
  /** The group's view, for a host that moves the reader's focus to it. */
  groupRef?: Ref<View>;
};

export function ChipRowFrame<T extends string | number>({
  theme,
  choices,
  chipRole,
  isSelected,
  onPressChip,
  label,
  chipA11yLabel,
  chipA11yHint,
  isDisabled,
  check,
  groupRole,
  groupA11yLabel,
  title,
  scroll = false,
  edgeInset = 0,
  follow: followed,
  wrapLabels = false,
  groupRef,
}: ChipRowFrameProps<T>) {
  const styles = makeStyles(theme);
  const follow = useFollowSelected(followed);
  const renderChip = (value: T) => (
    <Chip
      theme={theme}
      role={chipRole}
      prominent
      // Vertical only: horizontal slop would overlap the next chip.
      hitSlop={{ top: 8, bottom: 8 }}
      label={label(value)}
      accessibilityLabel={chipA11yLabel(value)}
      accessibilityHint={chipA11yHint?.(value)}
      selected={isSelected(value)}
      check={check}
      disabled={isDisabled?.(value) ?? false}
      onPress={() => onPressChip(value)}
      {...(wrapLabels ? { numberOfLines: 2, style: styles.wrapChip } : {})}
    />
  );
  const group = (
    <View
      ref={groupRef}
      style={scroll ? styles.line : styles.row}
      // RN's accessibilityRole has no "group": that one rides on `role` only.
      accessibilityRole={groupRole === "radiogroup" ? "radiogroup" : undefined}
      accessibilityLabel={groupA11yLabel}
      // react-native-web drops accessibilityRole="radiogroup": the ARIA props
      // reach the DOM on web and map natively too (as in Chip).
      role={groupRole}
      aria-label={groupA11yLabel}
    >
      {choices.map((value) =>
        scroll ? (
          <View key={String(value)} onLayout={follow.onChipLayout(value)}>
            {renderChip(value)}
          </View>
        ) : (
          <Fragment key={String(value)}>{renderChip(value)}</Fragment>
        ),
      )}
    </View>
  );
  return (
    <View style={styles.block}>
      {title ? (
        <Text style={styles.title} {...hiddenFromScreenReader()}>
          {title}
        </Text>
      ) : null}
      {scroll ? (
        // A ScrollView clips touches at its edge: the padding gives the chips'
        // vertical hitSlop room, the negative margin gives the height back.
        <ScrollView
          ref={follow.ref}
          onLayout={follow.onViewport}
          onScroll={follow.onScroll}
          scrollEventThrottle={32}
          horizontal
          style={styles.scroller}
          contentContainerStyle={[styles.scrollContent, edgeInset > 0 && { paddingHorizontal: edgeInset }]}
        >
          {group}
        </ScrollView>
      ) : (
        group
      )}
    </View>
  );
}

// Keeps the selected chip on screen in a scrolling row. Moves only when the
// chip is outside the visible part, so a tap on a visible chip never scrolls.
export function useFollowSelected<T extends string | number>(selected: T | null) {
  const ref = useRef<ScrollView>(null);
  const chips = useRef(new Map<T, { x: number; w: number }>());
  const viewport = useRef({ x: 0, w: 0 });
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    const chip = selected === null ? undefined : chips.current.get(selected);
    const { x, w } = viewport.current;
    if (!chip || w === 0) return;
    const to =
      chip.x < x ? chip.x : chip.x + chip.w > x + w ? chip.x + chip.w - w : null;
    if (to !== null) ref.current?.scrollTo({ x: Math.max(0, to), animated: !reducedMotion });
  }, [selected, reducedMotion]);
  return {
    ref,
    onChipLayout: (value: T) => (e: LayoutChangeEvent) => {
      const { x, width } = e.nativeEvent.layout;
      chips.current.set(value, { x, w: width });
    },
    onViewport: (e: LayoutChangeEvent) => {
      viewport.current = { ...viewport.current, w: e.nativeEvent.layout.width };
    },
    onScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      viewport.current = { ...viewport.current, x: e.nativeEvent.contentOffset.x };
    },
  };
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    block: { gap: s.s2 },
    title: { color: p.ink, fontSize: ty.sizes.sm, fontWeight: "600" },
    row: { flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: s.s2 },
    line: { flexDirection: "row", alignItems: "center", gap: s.s2 },
    scroller: { marginVertical: -s.s2 },
    scrollContent: { paddingVertical: s.s2 },
    wrapChip: { maxWidth: "100%" },
  });
}
